import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import { useToast } from '../components/ui/Toast';
import { parseApiError } from '../utils/apiError';

// Editable name/phone; email is read-only (it's the login identifier —
// changing it is a separate, not-yet-built self-service flow).
function ProfileDetailsForm() {
  const { user, updateProfile } = useAuth();
  const { addToast } = useToast();

  const [values, setValues] = useState({
    fName: user?.fName ?? '',
    lName: user?.lName ?? '',
    phone: user?.phone ?? '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const setField = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      const updated = await updateProfile(values);
      setValues({ fName: updated.fName ?? '', lName: updated.lName ?? '', phone: updated.phone ?? '' });
      addToast('Profile updated', { variant: 'success' });
    } catch (err) {
      const { formError: fe, fieldErrors: fld } = parseApiError(err);
      setFormError(fe);
      setFieldErrors(fld);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="ss-form" onSubmit={submit} noValidate>
      {formError && <p className="ss-form__error" role="alert">{formError}</p>}

      <div className="ss-field">
        <span className="ss-label">Email</span>
        <p style={{ margin: 0 }}>{user?.email}</p>
      </div>

      <Input
        label="First name"
        value={values.fName}
        onChange={setField('fName')}
        error={fieldErrors.fName}
        required
        autoFocus
      />
      <Input
        label="Last name"
        value={values.lName}
        onChange={setField('lName')}
        error={fieldErrors.lName}
        required
      />
      <Input
        label="Phone"
        value={values.phone}
        onChange={setField('phone')}
        error={fieldErrors.phone}
        required
      />

      <div className="ss-field">
        <div className="ss-label-row">
          <span className="ss-label">Password</span>
          <Link to="/profile/password" className="ss-btn ss-btn--ghost ss-btn--sm">Change password</Link>
        </div>
      </div>

      <div className="ss-form__actions">
        <Button type="submit" disabled={submitting}>
          {submitting ? <Spinner label="Saving" /> : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}

export default function Profile() {
  return (
    <section className="ss-container" style={{ paddingBlock: 'var(--ss-space-6)' }}>
      <h1>Your profile</h1>

      <div style={{ marginTop: 'var(--ss-space-5)', maxWidth: '32rem' }}>
        <Card>
          <h2 style={{ marginTop: 0 }}>Profile details</h2>
          <ProfileDetailsForm />
        </Card>
      </div>
    </section>
  );
}
